import React, { useState, useMemo, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { FiRefreshCw, FiAlertTriangle, FiAlertCircle, FiCalendar, FiDownload } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import Logo from '../common/Logo';
import { getAdminAlerts, AdminAlert, AdminAlertsResponse, AlertType } from '../../api/admin';
import { TRACK_NAMES, TRACK_TO_GRADE, GRADE_DISPLAY_NAMES } from '../../types/constants';
import { convertToCSV, downloadCSV, formatDateForFilename } from '../../utils/exportUtils';

// Alert type display names
const ALERT_TYPE_DISPLAY: Record<AlertType, string> = {
  'LOW_CQPM_PROGRESS': 'Low CQPM Progress',
  'CONSECUTIVE_FAILS': '3 Consecutive Fails',
};

// Helper function to convert Date to local YYYY-MM-DD string
const formatDateLocal = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Access denied component for non-admin users
const AccessDenied = () => (
  <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex flex-col justify-center p-6">
    <div className="max-w-4xl mx-auto text-center">
      <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
      <p className="text-base text-gray-600 mt-2">You are not authorized to view this page.</p>
    </div>
  </div>
);

// Header component
const AlertsDashboardHeader = () => (
  <h1 className="text-3xl font-bold text-center pt-4 mb-4 flex items-center justify-center gap-3">
    <Logo size={28} />
    <span className="bg-gradient-to-r from-orange-600 to-red-600 text-transparent bg-clip-text">
      Student Alerts Dashboard
    </span>
  </h1>
);

// Date range and filter selector component
interface DateRangeSelectorProps {
  startDate: string;
  endDate: string;
  alertTypeFilter: AlertType | '';
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onAlertTypeChange: (type: AlertType | '') => void;
  onSearch: () => void;
  onRefresh: () => void;
  onDownloadCSV: () => void;
  isLoading?: boolean;
  hasData?: boolean;
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  startDate,
  endDate,
  alertTypeFilter,
  onStartDateChange,
  onEndDateChange,
  onAlertTypeChange,
  onSearch,
  onRefresh,
  onDownloadCSV,
  isLoading = false,
  hasData = false,
}) => {
  return (
    <Card className="shadow-lg border border-gray-100 mb-4">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => onStartDateChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              disabled={isLoading}
            />
          </div>
          <div className="flex-1">
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => onEndDateChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              disabled={isLoading}
            />
          </div>
          <div className="flex-1">
            <label htmlFor="alertType" className="block text-sm font-medium text-gray-700 mb-2">
              Alert Type
            </label>
            <select
              id="alertType"
              value={alertTypeFilter}
              onChange={(e) => onAlertTypeChange(e.target.value as AlertType | '')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              disabled={isLoading}
            >
              <option value="">All Types</option>
              <option value="LOW_CQPM_PROGRESS">Low CQPM Progress</option>
              <option value="CONSECUTIVE_FAILS">3 Consecutive Fails</option>
            </select>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={onSearch}
              disabled={isLoading || !startDate || !endDate}
              className="bg-orange-600 hover:bg-orange-700 text-white px-6"
            >
              <FiCalendar className="w-4 h-4 mr-2" />
              Search
            </Button>
            <Button
              onClick={onRefresh}
              disabled={isLoading || !startDate || !endDate}
              variant="outline"
              className="flex items-center px-4"
            >
              <FiRefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              onClick={onDownloadCSV}
              disabled={isLoading || !hasData}
              variant="outline"
              className="flex items-center px-4 bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
            >
              <FiDownload className="w-4 h-4 mr-2" />
              CSV
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Summary stats component
interface SummaryStatsProps {
  totalAlerts: number;
  lowCqpmAlerts: number;
  consecutiveFailsAlerts: number;
  uniqueStudents: number;
  isLoading?: boolean;
}

const SummaryStats: React.FC<SummaryStatsProps> = ({
  totalAlerts,
  lowCqpmAlerts,
  consecutiveFailsAlerts,
  uniqueStudents,
  isLoading = false,
}) => {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="shadow-lg border border-gray-100">
            <CardContent className="p-4">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-gray-300 rounded w-1/2"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const stats = [
    {
      title: 'Total Alerts',
      value: totalAlerts,
      icon: FiAlertTriangle,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
    },
    {
      title: 'Low CQPM Progress',
      value: lowCqpmAlerts,
      icon: FiAlertCircle,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
    },
    {
      title: '3 Consecutive Fails',
      value: consecutiveFailsAlerts,
      icon: FiAlertTriangle,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
    },
    {
      title: 'Unique Students',
      value: uniqueStudents,
      icon: FiAlertCircle,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
      {stats.map((stat, index) => (
        <Card key={index} className="shadow-lg border border-gray-100">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">{stat.title}</p>
                <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
              </div>
              <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                <stat.icon className={`w-5 h-5 ${stat.color}`} />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// Alerts table component
interface AlertsTableProps {
  alerts: AdminAlert[];
  isLoading?: boolean;
}

const AlertsTable: React.FC<AlertsTableProps> = ({ alerts, isLoading = false }) => {
  if (isLoading) {
    return (
      <Card className="shadow-lg border border-gray-100">
        <CardContent className="p-4">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-gray-300 rounded w-1/3 mb-6"></div>
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-300 rounded"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (alerts.length === 0) {
    return (
      <Card className="shadow-lg border border-gray-100">
        <CardContent className="p-6 text-center">
          <div className="text-gray-400 mb-4">
            <FiAlertTriangle className="w-12 h-12 mx-auto" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Alerts Found</h3>
          <p className="text-gray-600">
            No student alerts found for the selected date range and filters.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-lg border border-gray-100">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Student Email
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current Track
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Knowledge Grade
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Alert Type
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Triggered Date
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {alerts.map((alert, index) => {
                const trackName =
                  TRACK_NAMES[alert.trackId as keyof typeof TRACK_NAMES] || alert.trackId;
                const gradeName =
                  GRADE_DISPLAY_NAMES[alert.knowledgeGrade] ||
                  `Grade ${alert.knowledgeGrade}`;
                const alertTypeName = ALERT_TYPE_DISPLAY[alert.alertType];

                let detailsText = '';
                if (alert.alertType === 'LOW_CQPM_PROGRESS' && alert.metadata?.slope !== undefined) {
                  detailsText = `Slope: ${alert.metadata.slope.toFixed(3)} CQPM/day`;
                } else if (
                  alert.alertType === 'CONSECUTIVE_FAILS' &&
                  alert.metadata?.consecutiveFailCount
                ) {
                  detailsText = `${alert.metadata.consecutiveFailCount} fails`;
                }

                return (
                  <tr
                    key={`${alert.userId}-${alert.trackId}-${alert.triggeredDate}-${alert.alertType}`}
                    className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                  >
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {alert.userEmail}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {trackName}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {gradeName}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          alert.alertType === 'CONSECUTIVE_FAILS'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {alertTypeName}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {new Date(alert.triggeredDate).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {detailsText}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// Error component
interface ErrorDisplayProps {
  error: string;
  onRetry: () => void;
}

const ErrorDisplay: React.FC<ErrorDisplayProps> = ({ error, onRetry }) => (
  <Card className="shadow-lg border border-red-100">
    <CardContent className="p-6 text-center">
      <div className="text-red-400 mb-4">
        <FiAlertTriangle className="w-12 h-12 mx-auto" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading Data</h3>
      <p className="text-gray-600 mb-4">{error}</p>
      <Button onClick={onRetry} className="bg-red-600 hover:bg-red-700 text-white">
        <FiRefreshCw className="w-4 h-4 mr-2" />
        Try Again
      </Button>
    </CardContent>
  </Card>
);

// Main component
export const AdminAlertsDashboard: React.FC = () => {
  const { user: currentUser } = useAuth();

  // Check if user is admin
  const isAdmin = useMemo(() => currentUser?.role === 'admin', [currentUser?.role]);

  // State
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7); // Default to last 7 days
    return formatDateLocal(date);
  });
  const [endDate, setEndDate] = useState(() => formatDateLocal(new Date()));
  const [alertTypeFilter, setAlertTypeFilter] = useState<AlertType | ''>('');
  const [data, setData] = useState<AdminAlertsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Calculate summary stats from data
  const summaryStats = useMemo(() => {
    if (!data || !data.alerts) {
      return {
        totalAlerts: 0,
        lowCqpmAlerts: 0,
        consecutiveFailsAlerts: 0,
        uniqueStudents: 0,
      };
    }

    const alerts = data.alerts;
    const lowCqpmAlerts = alerts.filter((a) => a.alertType === 'LOW_CQPM_PROGRESS').length;
    const consecutiveFailsAlerts = alerts.filter((a) => a.alertType === 'CONSECUTIVE_FAILS').length;
    const uniqueStudents = new Set(alerts.map((a) => a.userEmail)).size;

    return {
      totalAlerts: alerts.length,
      lowCqpmAlerts,
      consecutiveFailsAlerts,
      uniqueStudents,
    };
  }, [data]);

  // Fetch data function
  const fetchData = useCallback(async () => {
    // Validate date range - startDate must be before or equal to endDate
    if (startDate > endDate) {
      setError('Start date must be before or equal to end date');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await getAdminAlerts(
        startDate,
        endDate,
        alertTypeFilter || undefined
      );
      setData(result);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch alerts';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, alertTypeFilter]);

  // Retry function
  const handleRetry = useCallback(() => {
    fetchData();
  }, [fetchData]);

  // CSV download function
  const handleDownloadCSV = useCallback(() => {
    if (!data?.alerts || data.alerts.length === 0) return;

    // Define CSV headers with explicit typing
    const headers = [
      { key: 'userEmail' as const, label: 'Student Email' },
      { key: 'trackName' as const, label: 'Current Track' },
      { key: 'gradeName' as const, label: 'Knowledge Grade' },
      { key: 'alertTypeName' as const, label: 'Alert Type' },
      { key: 'triggeredDate' as const, label: 'Triggered Date' },
      { key: 'details' as const, label: 'Details' },
    ];

    // Transform data for CSV
    const csvData = data.alerts.map((alert) => {
      const trackName =
        TRACK_NAMES[alert.trackId as keyof typeof TRACK_NAMES] || alert.trackId;
      const gradeName =
        GRADE_DISPLAY_NAMES[alert.knowledgeGrade] || `Grade ${alert.knowledgeGrade}`;
      const alertTypeName = ALERT_TYPE_DISPLAY[alert.alertType];

      let details = '';
      if (alert.alertType === 'LOW_CQPM_PROGRESS' && alert.metadata?.slope !== undefined) {
        details = `Slope: ${alert.metadata.slope.toFixed(3)} CQPM/day`;
      } else if (
        alert.alertType === 'CONSECUTIVE_FAILS' &&
        alert.metadata?.consecutiveFailCount
      ) {
        details = `${alert.metadata.consecutiveFailCount} fails`;
      }

      return {
        userEmail: alert.userEmail,
        trackName,
        gradeName,
        alertTypeName,
        triggeredDate: new Date(alert.triggeredDate).toLocaleDateString(),
        details,
      };
    });

    const csvContent = convertToCSV(csvData, headers);
    const filename = `student-alerts-${startDate}-to-${endDate}-${formatDateForFilename()}.csv`;

    downloadCSV(csvContent, filename);
  }, [data, startDate, endDate]);

  // Early return if not admin
  if (!isAdmin) {
    return <AccessDenied />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white p-4">
      <div className="max-w-7xl mx-auto space-y-4">
        <AlertsDashboardHeader />

        {/* Navigation */}
        <div className="flex mb-4">
          <Link
            to="/admin"
            className="text-orange-600 hover:text-orange-800 transition-colors duration-300"
          >
            &larr; Back to Admin Dashboard
          </Link>
        </div>

        {/* Date Range and Filter Selector */}
        <DateRangeSelector
          startDate={startDate}
          endDate={endDate}
          alertTypeFilter={alertTypeFilter}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onAlertTypeChange={setAlertTypeFilter}
          onSearch={fetchData}
          onRefresh={fetchData}
          onDownloadCSV={handleDownloadCSV}
          isLoading={isLoading}
          hasData={!!(data?.alerts && data.alerts.length > 0)}
        />

        {/* Error Display */}
        {error && <ErrorDisplay error={error} onRetry={handleRetry} />}

        {/* Dashboard Content */}
        {!error && (
          <>
            {/* Summary Statistics */}
            <SummaryStats
              totalAlerts={summaryStats.totalAlerts}
              lowCqpmAlerts={summaryStats.lowCqpmAlerts}
              consecutiveFailsAlerts={summaryStats.consecutiveFailsAlerts}
              uniqueStudents={summaryStats.uniqueStudents}
              isLoading={isLoading}
            />

            {/* Alerts Table */}
            <AlertsTable alerts={data?.alerts || []} isLoading={isLoading} />
          </>
        )}
      </div>
    </div>
  );
};
